/**
 * Maps tenant brand colors from config.json onto the .v2 CSS variables.
 * Emitted as a stylesheet rule (not inline styles) so Radix portals, which
 * mount outside the V2Shell wrapper, inherit tenant branding through their
 * own .v2 class.
 */
export type TenantBrand = {
  primary?: string;
  secondary?: string;
};

const HEX_PATTERN = /^#[0-9a-f]{6}$/i;
const DARK_FOREGROUND = "#0f172a";
const LIGHT_FOREGROUND = "#ffffff";

function channelToLinear(channel: number): number {
  const srgb = channel / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  const value = parseInt(hex.slice(1), 16);
  const r = channelToLinear((value >> 16) & 0xff);
  const g = channelToLinear((value >> 8) & 0xff);
  const b = channelToLinear(value & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(hexA: string, hexB: string): number {
  const [darker, lighter] = [relativeLuminance(hexA), relativeLuminance(hexB)].sort(
    (a, b) => a - b
  );
  return (lighter + 0.05) / (darker + 0.05);
}

// NOTE: comparing WCAG contrastRatio(bg, light) vs contrastRatio(bg, dark)
// picks the dark foreground for mid-brightness reds like #ee3a43 (darkRatio
// 4.52 edges out lightRatio 3.95), which disagrees with the fixture data
// this module ships with. Perceived brightness (YIQ) matches all fixtures
// and is the standard heuristic for this kind of foreground pick.
function foregroundFor(background: string): string {
  const value = parseInt(background.slice(1), 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness >= 128 ? DARK_FOREGROUND : LIGHT_FOREGROUND;
}

function normalizeHex(value: string | undefined): string | null {
  if (!value || !HEX_PATTERN.test(value)) return null;
  return value.toLowerCase();
}

export function getTenantThemeVars(
  brand: TenantBrand | undefined
): Record<string, string> {
  const vars: Record<string, string> = {};
  const primary = normalizeHex(brand?.primary);
  if (primary) {
    vars["--v2-primary"] = primary;
    vars["--v2-primary-foreground"] = foregroundFor(primary);
    vars["--v2-ring"] = primary;
    vars["--v2-action"] = primary;
    vars["--v2-action-light"] = `${primary}1a`;
  }
  const secondary = normalizeHex(brand?.secondary);
  if (secondary) {
    vars["--v2-brand-secondary"] = secondary;
    vars["--v2-brand-secondary-foreground"] = foregroundFor(secondary);
  }
  return vars;
}

export function getTenantThemeCss(brand: TenantBrand | undefined): string {
  const vars = getTenantThemeVars(brand);
  const declarations = Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join("\n");
  return declarations ? `.v2 {\n${declarations}\n}` : "";
}
